import api from '@/api/api'
import cfg from '@root/config.js'

const EMPTY_DEAUTH_CONTACT = {
    id: null,
    contactType: null,
    contactValue: '',
    displayName: null,
    isVerified: null,
    isVerificationChecked: false,
    pendingRemovalId: null,
}

export default {
    data() {
        return {
            deauthContacts: [],
            initialDeauthContactIds: [],
            initialDeauthContactsById: {},
            initialDeauthContactsMetaById: {},
            deauthAllGroups: [],
            sendToReportRecipients: false,
            deauthAccessErrorMessage: '',
            deauthVerifyingIndices: [],
        }
    },
    computed: {
        deauthContactsValue() {
            return (this.deauthContacts || []).map(contact => String(contact?.contactValue ?? ''))
        },
        isSuperUser() {
            return cfg.adminsWhitelist.includes(this.$auth?.user?.sub)
        },
        canManageDeauthRecipients() {
            const role = String(this.project?.myRole || '').toLowerCase()
            return role === 'owner' || role === 'admin'
        },
    },
    methods: {
        getDeauthProjectId() {
            return String(
                this.project?.projectId || this.project?.id || this.$router.currentRoute.params.id || '',
            )
        },
        normalizeDeauthContactValue(value) {
            return String(value ?? '').trim()
        },
        normalizeDeauthContactsFromServer(contacts) {
            if (!Array.isArray(contacts) || contacts.length === 0) {
                return []
            }
            return contacts
                .filter(item => item && String(item?.contactValue ?? '').trim().length > 0)
                .map(item => ({
                    id: item.id ?? null,
                    contactType: item.contactType ?? null,
                    contactValue: String(item.contactValue ?? '').trim(),
                    displayName: item.displayName ?? null,
                    isVerified:
                        typeof item?.isVerified === 'boolean'
                            ? item.isVerified
                            : typeof item?.verified === 'boolean'
                              ? item.verified
                              : null,
                    isVerificationChecked: false,
                    pendingRemovalId: null,
                }))
        },
        mergeDeauthContactsPreservingOrder(previousContacts, serverContacts, mergedUnsavedContacts) {
            const serverPool = [...serverContacts]
            const unsavedPool = [...mergedUnsavedContacts]
            const result = []

            const takeFromServerById = id => {
                const sid = String(id ?? '')
                if (!sid) {
                    return null
                }
                const idx = serverPool.findIndex(contact => String(contact?.id ?? '') === sid)
                if (idx === -1) {
                    return null
                }
                return serverPool.splice(idx, 1)[0]
            }
            const takeFromServerByValue = value => {
                const normalizedValue = String(value ?? '').trim()
                if (!normalizedValue) {
                    return null
                }
                const idx = serverPool.findIndex(
                    contact => String(contact?.contactValue ?? '').trim() === normalizedValue,
                )
                if (idx === -1) {
                    return null
                }
                return serverPool.splice(idx, 1)[0]
            }
            const takeFromUnsavedByValue = value => {
                const normalizedValue = String(value ?? '').trim()
                if (!normalizedValue) {
                    return null
                }
                const idx = unsavedPool.findIndex(
                    contact => String(contact?.contactValue ?? '').trim() === normalizedValue,
                )
                if (idx === -1) {
                    return null
                }
                return unsavedPool.splice(idx, 1)[0]
            }

            for (const prev of previousContacts || []) {
                const prevId = String(prev?.id ?? '')
                const prevValue = String(prev?.contactValue ?? '').trim()

                if (!prevId && !prevValue) {
                    result.push({
                        ...EMPTY_DEAUTH_CONTACT,
                        pendingRemovalId: prev?.pendingRemovalId ?? null,
                    })
                    continue
                }

                let next = null
                if (prevId) {
                    next = takeFromServerById(prevId)
                }
                if (!next && prevValue) {
                    next = takeFromUnsavedByValue(prevValue) || takeFromServerByValue(prevValue)
                }
                if (next) {
                    result.push(next)
                }
            }

            result.push(...serverPool, ...unsavedPool)
            return result
        },
        async fetchDeauthSettings() {
            const projectId = this.getDeauthProjectId()
            if (!projectId) {
                return
            }

            try {
                const previousContacts = this.deauthContacts || []
                const previousById = new Map(
                    previousContacts
                        .filter(item => item && item.id)
                        .map(item => [String(item.id), item]),
                )
                const previousByValue = new Map(
                    previousContacts
                        .filter(item => item && String(item?.contactValue ?? '').trim().length > 0)
                        .map(item => [String(item.contactValue ?? '').trim(), item]),
                )
                const localUnsavedContacts = (this.deauthContacts || [])
                    .filter(item => !item?.id && String(item?.contactValue ?? '').trim().length > 0)
                    .map(item => ({
                        id: null,
                        contactType: item.contactType ?? null,
                        contactValue: String(item.contactValue ?? '').trim(),
                        displayName: item.displayName ?? null,
                        isVerified: item.isVerified ?? null,
                        isVerificationChecked: Boolean(item.isVerificationChecked),
                        pendingRemovalId: item.pendingRemovalId ?? null,
                    }))
                const localModifiedSavedById = (this.deauthContacts || []).reduce((acc, item) => {
                    const id = String(item?.id ?? '').trim()
                    if (!id) {
                        return acc
                    }
                    const currentValue = String(item?.contactValue ?? '').trim()
                    const initialValue = String(this.initialDeauthContactsById?.[id] ?? '').trim()
                    if (!currentValue || currentValue === initialValue) {
                        return acc
                    }
                    acc[id] = {
                        ...item,
                        contactValue: currentValue,
                        displayName: null,
                        isVerified: null,
                        isVerificationChecked: false,
                    }
                    return acc
                }, {})

                const res = await api.deauthNotification.getSettings(projectId)
                const errorPayload = res?.error || res?.data?.error
                if (Number(errorPayload?.code) === 403) {
                    this.deauthAccessErrorMessage = String(errorPayload?.message || 'not enough permissions')
                    this.deauthContacts = []
                    this.sendToReportRecipients = false
                    return
                }

                this.deauthAccessErrorMessage = ''
                const payload = res?.data?.data ?? res?.data ?? {}
                const serverContacts = this.normalizeDeauthContactsFromServer(payload?.contacts).map(item => {
                    const itemId = String(item?.id ?? '').trim()
                    const localModified = itemId ? localModifiedSavedById[itemId] : null
                    if (localModified) {
                        return {
                            ...localModified,
                            pendingRemovalId: localModified.pendingRemovalId ?? null,
                        }
                    }
                    const byId = item?.id ? previousById.get(String(item.id)) : null
                    const byValue = previousByValue.get(String(item?.contactValue ?? '').trim())
                    const prev = byId || byValue
                    return {
                        ...item,
                        isVerificationChecked: prev?.isVerificationChecked ?? false,
                        pendingRemovalId: prev?.pendingRemovalId ?? null,
                    }
                })
                const serverValues = new Set(
                    serverContacts.map(item => String(item?.contactValue ?? '').trim()),
                )
                const mergedUnsavedContacts = localUnsavedContacts.filter(
                    item => !serverValues.has(String(item?.contactValue ?? '').trim()),
                )
                this.deauthContacts = this.mergeDeauthContactsPreservingOrder(
                    previousContacts,
                    serverContacts,
                    mergedUnsavedContacts,
                )
                if (this.deauthContacts.length === 0) {
                    this.deauthContacts = [{ ...EMPTY_DEAUTH_CONTACT }]
                }
                this.initialDeauthContactIds = this.deauthContacts
                    .map(item => String(item?.id ?? ''))
                    .filter(id => id.length > 0)
                this.initialDeauthContactsById = this.deauthContacts.reduce((acc, item) => {
                    const id = String(item?.id ?? '').trim()
                    if (!id) {
                        return acc
                    }
                    acc[id] = String(item?.contactValue ?? '').trim()
                    return acc
                }, {})
                this.initialDeauthContactsMetaById = this.deauthContacts.reduce((acc, item) => {
                    const id = String(item?.id ?? '').trim()
                    if (!id) {
                        return acc
                    }
                    acc[id] = {
                        contactType: item?.contactType ?? null,
                        displayName: item?.displayName ?? null,
                        isVerified: typeof item?.isVerified === 'boolean' ? item.isVerified : null,
                    }
                    return acc
                }, {})
                this.sendToReportRecipients = Boolean(payload?.sendToReportRecipients)
            } catch (e) {
                this.deauthAccessErrorMessage = ''
                this.$store.dispatch('callNotify', 'Ошибка при загрузке настроек уведомлений о деавторизации')
            }
        },
        async fetchDeauthAllGroups() {
            if (!this.isSuperUser) {
                return
            }
            try {
                const res = await api.scheduledReport.getTgChats()
                const list = Array.isArray(res?.data) ? res.data : []
                this.deauthAllGroups = list
                    .filter(item => item && item.id !== undefined && item.name !== undefined)
                    .map(item => ({ id: String(item.id), name: String(item.name) }))
            } catch (e) {
                this.deauthAllGroups = []
            }
        },
        async loadDeauthNotificationRecipients() {
            await this.fetchDeauthSettings()
            await this.fetchDeauthAllGroups()
        },
        updateDeauthContactsValue(newValues) {
            const values = Array.isArray(newValues) ? newValues : []
            const previous = this.deauthContacts || []
            const used = new Set()
            const normalize = value => this.normalizeDeauthContactValue(value)

            const findUnusedByValue = trimmed =>
                previous.findIndex(
                    (contact, index) => !used.has(index) && normalize(contact?.contactValue) === trimmed,
                )
            const findFirstUnused = () => previous.findIndex((_, index) => !used.has(index))

            this.deauthContacts = values.map((value, index) => {
                const trimmed = normalize(value)
                let prevIndex = -1

                if (index < previous.length && !used.has(index)) {
                    const previousAtIndex = previous[index]
                    if (!trimmed || normalize(previousAtIndex?.contactValue) === trimmed) {
                        prevIndex = index
                    }
                }
                if (prevIndex === -1) {
                    const byValueIndex = findUnusedByValue(trimmed)
                    if (byValueIndex !== -1) {
                        prevIndex = byValueIndex
                    } else {
                        const firstUnusedIndex = findFirstUnused()
                        if (firstUnusedIndex !== -1) {
                            prevIndex = firstUnusedIndex
                        }
                    }
                }

                if (prevIndex === -1) {
                    return { ...EMPTY_DEAUTH_CONTACT, contactValue: trimmed }
                }

                used.add(prevIndex)
                const prev = previous[prevIndex]
                if (normalize(prev.contactValue) === trimmed) {
                    return prev
                }

                const prevId = String(prev?.id ?? '').trim()
                const initialValueById = normalize(this.initialDeauthContactsById?.[prevId])
                const initialMetaById = this.initialDeauthContactsMetaById?.[prevId] || {}
                const isRevertedToInitialSavedValue =
                    prevId.length > 0 &&
                    trimmed.length > 0 &&
                    initialValueById.length > 0 &&
                    trimmed === initialValueById

                if (isRevertedToInitialSavedValue) {
                    return {
                        ...prev,
                        contactValue: trimmed,
                        contactType: initialMetaById?.contactType ?? prev?.contactType ?? null,
                        displayName: initialMetaById?.displayName ?? null,
                        isVerified:
                            typeof initialMetaById?.isVerified === 'boolean' ? initialMetaById.isVerified : null,
                        isVerificationChecked: false,
                        pendingRemovalId: null,
                        verificationRequestFailed: false,
                    }
                }

                return {
                    ...prev,
                    contactValue: trimmed,
                    displayName: null,
                    isVerified: null,
                    isVerificationChecked: false,
                    pendingRemovalId: null,
                    verificationRequestFailed: false,
                }
            })
        },
        async updateSendToReportRecipients(value) {
            const newVal = Boolean(value)
            this.sendToReportRecipients = newVal
            const projectId = this.getDeauthProjectId()
            if (!projectId) {
                return
            }
            try {
                await api.deauthNotification.switchSendToReportRecipients({
                    projectId,
                    sendToReportRecipients: newVal,
                })
            } catch (e) {
                this.$store.dispatch('callNotify', 'Ошибка при обновлении настроек уведомлений')
            }
        },
        async removeDeauthContact(index) {
            const contact = this.deauthContacts[index]
            if (!contact) {
                return
            }

            this.deauthContacts = this.deauthContacts.filter((_, contactIndex) => contactIndex !== index)

            const contactId = String(contact.id ?? '')
            const pendingRemovalId = String(contact.pendingRemovalId ?? '')
            const candidateId = contactId || pendingRemovalId
            const shouldRemoveOnBackend =
                candidateId &&
                (this.initialDeauthContactIds.includes(candidateId) || Boolean(pendingRemovalId))
            if (!shouldRemoveOnBackend) {
                return
            }

            const projectId = this.getDeauthProjectId()
            if (!projectId) {
                return
            }

            try {
                await api.deauthNotification.removeContact({ projectId, id: candidateId })
                this.initialDeauthContactIds = this.initialDeauthContactIds.filter(id => id !== candidateId)
                await this.fetchDeauthSettings()
            } catch (e) {
                this.$store.dispatch('callNotify', 'Ошибка при удалении контакта')
            }
        },
        async verifyDeauthContact(index) {
            let contact = this.deauthContacts[index]
            if (!contact) {
                return
            }

            const projectId = this.getDeauthProjectId()
            if (!projectId) {
                return
            }

            const normalizedCurrentValue = this.normalizeDeauthContactValue(contact.contactValue)
            const hasDuplicate = this.deauthContacts.some(
                (item, itemIndex) =>
                    itemIndex !== index &&
                    this.normalizeDeauthContactValue(item?.contactValue) === normalizedCurrentValue &&
                    normalizedCurrentValue.length > 0,
            )
            if (hasDuplicate && !contact.id) {
                this.$store.dispatch('callNotify', 'Такое значение уже добавлено')
                return
            }

            const prev = Array.isArray(this.deauthVerifyingIndices) ? this.deauthVerifyingIndices : []
            this.deauthVerifyingIndices = prev.includes(index) ? prev : [...prev, index]

            try {
                this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, verificationRequestFailed: false } : item,
                )
                contact = this.deauthContacts[index]
                const existingId = String(contact.id ?? '').trim()
                const normalizedValue = String(contact.contactValue ?? '').trim()

                if (existingId) {
                    const initialValue = String(this.initialDeauthContactsById?.[existingId] ?? '').trim()
                    if (normalizedValue && normalizedValue !== initialValue) {
                        const updateRes = await api.deauthNotification.updateContact({
                            projectId,
                            id: existingId,
                            contactValue: normalizedValue,
                        })
                        const updated = updateRes?.data?.data ?? updateRes?.data ?? {}
                        this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                            itemIndex === index
                                ? {
                                      ...item,
                                      contactType: updated.contactType ?? item.contactType ?? null,
                                      contactValue: updated.contactValue ?? item.contactValue,
                                      displayName: updated.displayName ?? null,
                                      isVerified:
                                          typeof updated?.isVerified === 'boolean'
                                              ? updated.isVerified
                                              : typeof updated?.verified === 'boolean'
                                                ? updated.verified
                                                : null,
                                      isVerificationChecked: false,
                                      pendingRemovalId: null,
                                  }
                                : item,
                        )
                        this.initialDeauthContactsById = {
                            ...(this.initialDeauthContactsById || {}),
                            [existingId]: String(updated.contactValue ?? normalizedValue).trim(),
                        }
                        this.initialDeauthContactsMetaById = {
                            ...(this.initialDeauthContactsMetaById || {}),
                            [existingId]: {
                                contactType: updated.contactType ?? contact.contactType ?? null,
                                displayName: updated.displayName ?? null,
                                isVerified:
                                    typeof updated?.isVerified === 'boolean'
                                        ? updated.isVerified
                                        : typeof updated?.verified === 'boolean'
                                          ? updated.verified
                                          : null,
                            },
                        }
                        contact = this.deauthContacts[index]
                    }
                }

                if (!contact.id) {
                    const saveRes = await api.deauthNotification.updateContacts({
                        projectId,
                        contactValue: normalizedValue,
                    })
                    const saved = saveRes?.data?.data ?? saveRes?.data ?? {}
                    const savedId = String(saved.id ?? '')

                    this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                        itemIndex === index
                            ? {
                                  ...item,
                                  id: savedId || null,
                                  contactType: saved.contactType ?? item.contactType ?? null,
                                  contactValue: saved.contactValue ?? item.contactValue,
                                  displayName: saved.displayName ?? null,
                                  isVerified:
                                      typeof saved?.isVerified === 'boolean'
                                          ? saved.isVerified
                                          : typeof saved?.verified === 'boolean'
                                            ? saved.verified
                                            : null,
                                  isVerificationChecked: false,
                                  pendingRemovalId: null,
                              }
                            : item,
                    )
                    await this.fetchDeauthSettings()
                    contact =
                        this.deauthContacts.find(item => String(item?.id ?? '') === savedId) ||
                        this.deauthContacts[index]
                }

                const contactAfterSave = contact?.id ? contact : this.deauthContacts[index]
                if (!contactAfterSave?.id) {
                    return
                }

                const targetId = String(contactAfterSave.id)
                const verifyRes = await api.deauthNotification.verifyContacts({
                    projectId,
                    contactId: targetId,
                })
                const actualIndex = this.deauthContacts.findIndex(item => String(item?.id ?? '') === targetId)
                const applyIndex = actualIndex !== -1 ? actualIndex : index

                if (verifyRes?.error != null) {
                    this.$store.dispatch('callNotify', 'Ошибка при проверке контакта')
                    this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                        itemIndex === applyIndex
                            ? {
                                  ...item,
                                  verificationRequestFailed: true,
                                  isVerificationChecked: false,
                              }
                            : item,
                    )
                    return
                }

                const verified = verifyRes?.data?.data ?? verifyRes?.data ?? {}
                this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                    itemIndex === applyIndex
                        ? {
                              ...item,
                              displayName: Object.prototype.hasOwnProperty.call(verified, 'displayName')
                                  ? verified.displayName ?? null
                                  : item.displayName ?? null,
                              isVerified:
                                  typeof verified?.isVerified === 'boolean'
                                      ? verified.isVerified
                                      : typeof verified?.verified === 'boolean'
                                        ? verified.verified
                                        : false,
                              isVerificationChecked: true,
                              verificationRequestFailed: false,
                          }
                        : item,
                )
            } catch (e) {
                this.$store.dispatch('callNotify', 'Ошибка при проверке контакта')
                this.deauthContacts = this.deauthContacts.map((item, itemIndex) =>
                    itemIndex === index
                        ? {
                              ...item,
                              verificationRequestFailed: true,
                              isVerificationChecked: false,
                          }
                        : item,
                )
            } finally {
                const current = Array.isArray(this.deauthVerifyingIndices) ? this.deauthVerifyingIndices : []
                this.deauthVerifyingIndices = current.filter(itemIndex => itemIndex !== index)
            }
        },
        resetDeauthNotificationRecipientsState() {
            this.deauthContacts = []
            this.initialDeauthContactIds = []
            this.initialDeauthContactsById = {}
            this.initialDeauthContactsMetaById = {}
            this.deauthAllGroups = []
            this.sendToReportRecipients = false
            this.deauthAccessErrorMessage = ''
            this.deauthVerifyingIndices = []
        },
    },
}
